import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";
import type { 
  CreateTemplateTaskInput, 
  UpdateTemplateTaskInput, 
  UpdateSectionInput,
  CreateSectionInput
} from "@/types/Admin";
import { toast } from "@/components/ui/toast";
import { logger } from "@/services/logger";

/** Manages create section state and operations. */
export function useCreateSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateSectionInput) => adminService.createSection(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-sections'] });
      toast.success("Section created");
    },
    onError: (error) => {
      toast.error("Failed to create section");
      logger.error("Failed to create section", error);
    }
  });
}

/** Manages playbook sections state and operations. */
export function usePlaybookSections() {
  return useQuery({
    queryKey: ['playbook-sections'],
    queryFn: () => adminService.getSections(),
  });
}

/** Manages playbook template tasks state and operations. */
export function usePlaybookTemplateTasks() {
  return useQuery({
    queryKey: ['playbook-templates'],
    queryFn: () => adminService.getTemplateTasks(),
  });
}

/** Manages update section state and operations. */
export function useUpdateSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateSectionInput }) =>
      adminService.updateSection(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-sections'] });
      toast.success("Section updated");
    },
    onError: (error) => {
      toast.error("Failed to update section");
      logger.error("Failed to update section", error);
    }
  });
}

/** Manages update section order state and operations. */
export function useUpdateSectionOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (sections: { id: string; sort_order: number }[]) =>
      adminService.updateSectionOrder(sections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-sections'] });
      toast.success("Section order updated");
    },
    onError: (error) => {
      toast.error("Failed to update section order");
      logger.error("Failed to update section order", error);
    }
  });
}

/** Manages create template task state and operations. */
export function useCreateTemplateTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateTemplateTaskInput) =>
      adminService.createTemplateTask(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-templates'] });
      toast.success("Task template created");
    },
    onError: (error) => {
      toast.error("Failed to create task template");
      logger.error("Failed to create task template", error);
    }
  });
}

/** Manages update template task state and operations. */
export function useUpdateTemplateTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTemplateTaskInput }) =>
      adminService.updateTemplateTask(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-templates'] });
      toast.success("Task template updated");
    },
    onError: (error) => {
      toast.error("Failed to update task template");
      logger.error("Failed to update task template", error);
    }
  });
}

/** Manages delete template task state and operations. */
export function useDeleteTemplateTask() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => adminService.deleteTemplateTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-templates'] });
      toast.success("Task template deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete task template");
      logger.error("Failed to delete task template", error);
    }
  });
}

/** Manages update task order state and operations. */
export function useUpdateTaskOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (tasks: { id: string; section_key: string; sort_order: number }[]) =>
      adminService.updateTaskOrder(tasks),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-templates'] });
      toast.success("Task order updated");
    },
    onError: (error) => {
      toast.error("Failed to update task order");
      logger.error("Failed to update task order", error);
    }
  });
}
